import { ReactElement } from 'react';
import { getPriorityColor, getclasses } from '../_helpers/dashboardHelper';
import { useData } from '../context/data.context';
import {
  DataWidgetMinMaxAttribute,
  DeviceSummaryAttribute,
  ParameterAttribute,
  ParametersType,
  ParticleData,
  PriorityList,
  DeviceType
} from '../constants/telosair-interfaces';
import { getDisplayableValue } from '../_helpers/globalHelper';
import { ParameterTooltips } from '../constants/global-constants';
import { STALE_TEXT_CLASS } from '../_helpers/staleData';

export type ModifiedDeviceWiseDataAttribute = DeviceSummaryAttribute & { serialNumber: number };

type ParameterCardProps = {
  parameterProperties: ParameterAttribute;
  minMaxData: DataWidgetMinMaxAttribute | null;
  particleData: ParticleData;
  type: DeviceType;
  /** The readings are a last known fallback; the value is drawn grey. */
  stale?: boolean;
  onClick: (parameter: ParametersType, serialNumber: number) => void;
};

/**
 * ParameterCard component for displaying a parameter's value and status.
 * @param props - Properties of Parameter Card Component.
 * @returns  A React element representing the ParameterCard component.
 */
export default function ParameterCard(props: ParameterCardProps): ReactElement {
  const { parameterProperties, minMaxData, onClick, particleData, type, stale = false } = props;
  const { userConfigDataV2, clientThresholdSettings, loading } = useData();
  // Readings arrive from the API already expressed in the user's selected unit
  // (the backend applies the unit config), so they are displayed as-is.
  const parameterValue = particleData && particleData[parameterProperties.parameterName];
  const maxValue = minMaxData?.max?.[parameterProperties.parameterName];
  const minValue = minMaxData?.min?.[parameterProperties.parameterName];
  const priority = getPriorityColor(
    parameterProperties.parameterName,
    parameterValue,
    clientThresholdSettings,
    userConfigDataV2,
    type
  );

  const priorityForMax =
    minMaxData?.max &&
    Object.prototype.hasOwnProperty.call(minMaxData?.max, parameterProperties.parameterName)
      ? getPriorityColor(
          parameterProperties.parameterName,
          minMaxData?.max[parameterProperties.parameterName] || 0,
          clientThresholdSettings,
          userConfigDataV2,
          type
        )
      : PriorityList.none;

  const priorityForMin =
    minMaxData?.min &&
    Object.prototype.hasOwnProperty.call(minMaxData?.min, parameterProperties.parameterName)
      ? getPriorityColor(
          parameterProperties.parameterName,
          minMaxData?.min[parameterProperties.parameterName] || 0,
          clientThresholdSettings,
          userConfigDataV2,
          type
        )
      : PriorityList.none;

  return (
    <button
      disabled
      onClick={() => onClick(parameterProperties.parameterName, 1)}
      type="button"
      className="flex flex-1 flex-col justify-around items-center text-center p-1 py-2 rounded dark:bg-background-500"
    >
      <div className="flex justify-center items-center">
        <p className="text-sm font-medium dark:text-gray-300">{parameterProperties.shortName}</p>
        <span className="ml-1 text-xs text-gray-400">
          {(userConfigDataV2?.config?.unit &&
            userConfigDataV2?.config?.unit[parameterProperties.parameterName]) ||
            ''}
        </span>
        <div className="relative group/tooltip ml-1">
          <span className="material-icons-outlined cursor-pointer text-base leading-4 text-gray-400">
            info
          </span>
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 w-60 p-2 text-xs text-white bg-gray-900 rounded-md transition-all duration-100 scale-0 origin-top group-hover/tooltip:scale-100">
            <p className="text-start">{ParameterTooltips[parameterProperties.parameterName]}</p>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center gap-1 flex-col">
        <div
          className={`py-2 text-2xl font-bold ${
            stale ? STALE_TEXT_CLASS : getclasses(true, priority)
          }`}
        >
          {getDisplayableValue(parameterProperties.parameterName, parameterValue)}
        </div>
        <div className="flex justify-center font-semibold items-center text-xs py-1 text-gray-400 rounded">
          <div className={`flex items-center px-1 `}>
            H:{' '}
            <span className={`${getclasses(true, priorityForMax)} ml-1`}>
              {loading || !minMaxData?.max
                ? '-'
                : getDisplayableValue(parameterProperties.parameterName, maxValue)}
            </span>
          </div>
          <div className={`flex items-center px-1 `}>
            L:{' '}
            <span className={`${getclasses(true, priorityForMin)} ml-1`}>
              {loading || !minMaxData?.min
                ? '-'
                : getDisplayableValue(parameterProperties.parameterName, minValue)}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
